import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

import ChangeEmailPart from './ChangeEmailPart'
import ChangePasswordPart from './ChangePasswordPart'
import CurrentCredentialSection from './CurrentCredentialSection'
import { Button, Modal, SectionSubtitle, Spinner } from '../..'
import { useUser } from '../../../context'
import { Student, Profesor } from '../../../types'
import { Spacer } from '../../global'
import { ChangeCredentialsContainer } from '../styles'

const INITIAL_CREDENTIALS = {
  currentPassword: '',
  password: '',
  confirmPassword: '',
  email: '',
  confirmEmail: '',
  currentEmail: '',
}

const CredentialsSection = ({
  type,
  editedProfile,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleChangeEmail,
  showCredentials,
  setShowCredentials,
}: {
  type: string | null
  editedProfile: Student | Profesor | null
  handleChangeEmail: (value: string) => void
  showCredentials: boolean
  setShowCredentials: Dispatch<SetStateAction<boolean>>
}) => {
  const { changeEmail, changePassword } = useUser()

  const [credentials, setCredentials] = useState(INITIAL_CREDENTIALS)

  const [status, setStatus] = useState({
    success: false,
    error: false,
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const handleCredentials = (value: string, filterName: string) => {
    setCredentials({ ...credentials, [filterName]: value })
  }

  const isChangingPassword =
    credentials.password !== '' && credentials.confirmPassword !== ''
  const isChangingEmail =
    credentials.email !== '' && credentials.confirmEmail !== ''

  const disabledButton =
    credentials.currentPassword === '' ||
    credentials.currentEmail === '' ||
    (isChangingPassword &&
      (credentials.password === '' ||
        credentials.confirmPassword === '' ||
        credentials.password !== credentials.confirmPassword)) ||
    (isChangingEmail &&
      (credentials.email === '' ||
        credentials.confirmEmail === '' ||
        credentials.email !== credentials.confirmEmail)) ||
    (!isChangingPassword && !isChangingEmail)
      ? true
      : false

  const handleSaveCredentials = async () => {
    if (!editedProfile) return
    const { email, confirmPassword, currentPassword } = credentials
    setLoading(true)
    try {
      setStatus({ success: false, error: false, message: '' })

      if (isChangingEmail) {
        await changeEmail(email, editedProfile.role)
      }

      if (isChangingPassword) {
        await changePassword(currentPassword, confirmPassword)
      }

      setStatus({
        success: true,
        error: false,
        message: 'Los cambios se realizaron exitosamente',
      })

      setCredentials(INITIAL_CREDENTIALS)

      setShowCredentials(false)
    } catch (error) {
      setStatus({
        success: false,
        error: true,
        message: error instanceof Error ? error.message : 'Ocurrió un error',
      })
    } finally {
      setLoading(false)
      setShowModal(true)
    }
  }

  const handleModalClose = () => {
    setShowModal(false)
    setStatus({ success: false, error: false, message: '' })
    setCredentials(INITIAL_CREDENTIALS)
  }

  useEffect(() => {
    if (!showCredentials) {
      setCredentials(INITIAL_CREDENTIALS)
    }
  }, [showCredentials])

  return (
    <>
      {showModal && (
        <Modal onClose={() => handleModalClose()}>
          <Spacer height={30} />
          <SectionSubtitle fontSize={16} fontWeight={500} textAlign="center">
            <>{status.message}</>
          </SectionSubtitle>
          <Spacer height={30} />
        </Modal>
      )}
      {editedProfile && showCredentials && (
        <>
          {loading ? (
            <Spinner />
          ) : (
            <>
              <Spacer height={40} />
              <SectionSubtitle fontWeight={500} fontSize={16} lineHeight={21}>
                Credenciales de acceso
              </SectionSubtitle>

              <>
                <Spacer height={20} />
                <SectionSubtitle fontWeight={500} fontSize={14} lineHeight={21}>
                  Ingresa tus credenciales actuales para confirmar los cambios
                </SectionSubtitle>
              </>

              <Spacer height={20} />
              {type !== 'new-user' && (
                <>
                  <CurrentCredentialSection
                    credentials={credentials}
                    handleChange={handleCredentials}
                    inputClass="profile-input"
                  />
                  <Spacer height={40} />
                  <SectionSubtitle
                    fontWeight={500}
                    fontSize={14}
                    lineHeight={21}
                  >
                    Ingresa tus nuevas credenciales
                  </SectionSubtitle>
                  <Spacer height={20} />
                </>
              )}

              <ChangeCredentialsContainer>
                <ChangePasswordPart
                  formType={type}
                  credentials={credentials}
                  handleChange={handleCredentials}
                  inputClass="profile-input"
                />

                <ChangeEmailPart
                  credentials={credentials}
                  handleChange={handleCredentials}
                  inputClass="profile-input"
                />
              </ChangeCredentialsContainer>
              <Spacer height={50} />

              <div className="d-flex justify-content-center">
                {!isChangingPassword && !isChangingEmail ? (
                  <Button
                    className="dark-grey"
                    onClick={() => setShowCredentials(false)}
                    label="Cerrar credenciales"
                    fontSize={16}
                  />
                ) : (
                  <Button
                    className="dark-grey"
                    onClick={handleSaveCredentials}
                    disabled={disabledButton}
                    label="Actualizar credenciales"
                    fontSize={16}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}

export default CredentialsSection
